/*
 * Instant search (native, ConvertCart-parity replacement).
 * Renders the real search results (sections/instant-search-results.liquid) into
 * a dropdown (desktop) / full-screen layer (mobile), with working facets + sort,
 * reusing product-card (so case pricing + tag icons + add-to-cart come for free).
 *
 * Markup contract (see snippets/predictive-search.liquid):
 *   <instant-search>
 *     <form class="search" action="/search"> ... <input class="js-search-input"> ... </form>
 *     <div class="instant-search__panel" data-instant-panel hidden>
 *       <div class="instant-search__suggestions" data-instant-suggestions></div>
 *       <div class="instant-search__results" data-instant-results></div>
 *     </div>
 *     <div class="overlay js-search-overlay"></div>
 *   </instant-search>
 */
if (!customElements.get('instant-search')) {
  class InstantSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.form = this.input ? this.input.closest('form') : this.querySelector('form');
      this.panel = this.querySelector('[data-instant-panel]');
      this.resultsEl = this.querySelector('[data-instant-results]');
      this.suggestionsEl = this.querySelector('[data-instant-suggestions]');
      this.overlay = this.querySelector('.js-search-overlay');
      this.section = this.getAttribute('data-section') || 'instant-search-results';
      this.minChars = parseInt(this.getAttribute('data-min-chars'), 10) || 2;
      this.searchBase = (this.form && this.form.getAttribute('action')) || (window.theme && theme.routes && theme.routes.searchUrl) || '/search';
      this.currentUrl = null;       // last results URL fetched (no section_id) — used for View All / sort
      this.debounceTimer = null;
      this.controller = null;       // aborts the in-flight results (section) fetch
      this.suggestController = null;// aborts the in-flight suggestions fetch
      this._reqId = 0;              // monotonic id so only the latest request injects/opens
      this.fallbackThreshold = 5;   // raw results below this => use the best query suggestion (partial-word match)
      this._completion = null;      // { from, to } when a partial term was completed, so submit matches the dropdown
    }

    connectedCallback() {
      if (!this.input || !this.panel) return;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', () => { if (this.resultsEl.innerHTML.trim()) this.open(); });
      // Submitting (Enter / search button) on a partial term should search the COMPLETED term,
      // so the results page matches the products shown in the dropdown (not the raw partial).
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Delegated interactions inside the injected results.
      this.resultsEl.addEventListener('click', this.onResultsClick.bind(this));
      this.resultsEl.addEventListener('change', this.onResultsChange.bind(this));
      if (this.suggestionsEl) this.suggestionsEl.addEventListener('click', this.onSuggestionClick.bind(this));

      // Mobile filter/sort bar (static chrome) lives in the panel.
      this.panel.addEventListener('click', this.onPanelChrome.bind(this));

      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

      const reset = this.querySelector('.js-search-reset');
      if (reset) reset.addEventListener('click', () => { this.clear(); });

      // Close the dropdown when a drawer/modal opens — e.g. clicking add-to-cart / "Choose
      // options" on a result opens the quick-add (or cart) drawer; without this the dropdown
      // stays open behind it and overlaps. The theme adds body.fixed when any drawer opens.
      this.bodyObserver = new MutationObserver(() => {
        if (this.hasAttribute('open') && document.body.classList.contains('fixed')) this.close();
      });
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      // Close on any click outside the dropdown/input. Robust regardless of the theme's base
      // .overlay { pointer-events:none }: a click on the (transparent) overlay OR on page content
      // behind it both resolve to "outside" here.
      this._onDocClick = (e) => {
        if (!this.hasAttribute('open')) return;
        const onOverlay = this.overlay && this.overlay.contains(e.target);
        if (this.contains(e.target) && !onOverlay) return;
        this.close();
      };
      document.addEventListener('click', this._onDocClick);
    }

    onInput() {
      const term = this.input.value.trim();
      clearTimeout(this.debounceTimer);
      if (term.length < this.minChars) {
        this._reqId++;                                  // cancel any in-flight request
        if (this.controller) this.controller.abort();
        this.close();
        this.resultsEl.innerHTML = '';
        return;
      }
      this.debounceTimer = setTimeout(() => this.typeSearch(term), 250);
    }

    /* Typing path. Native full-text search matches whole words well but partial words
       poorly (e.g. "rober" -> 1 result, "oatle" -> 4). When the raw term is sparse we
       re-run with the best query suggestion ("robert oatley" -> 33), which matches AND
       still returns facets — so the filter sidebar stays correct. */
    async typeSearch(term) {
      const reqId = ++this._reqId;
      const suggestP = this.fetchSuggestions(term);     // left column + best completion
      const r1 = await this.fetchSectionText(this.buildSearchUrl(term));
      if (!r1 || reqId !== this._reqId) { suggestP.catch(() => {}); return; }
      if (r1.count < this.fallbackThreshold) {
        const top = await suggestP;
        if (reqId !== this._reqId) return;
        if (top && top.toLowerCase() !== term.toLowerCase()) {
          const topUrl = this.buildSearchUrl(top);
          const r2 = await this.fetchSectionText(topUrl);
          if (!r2 || reqId !== this._reqId) return;
          this._completion = { from: term, to: top };   // remember for submit / View All matching
          this.injectText(topUrl, r2.sectionHTML);
          return;
        }
      }
      this._completion = null;
      this.injectText(this.buildSearchUrl(term), r1.sectionHTML);
      suggestP.catch(() => {});
    }

    buildSearchUrl(term) {
      return `${this.searchBase}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
    }

    /* Full results-page URL (mirrors a native submit: keeps the form's `type`, adds prefix). */
    buildPageUrl(term) {
      const typeEl = this.form && this.form.querySelector('[name="type"]');
      const type = typeEl && typeEl.value ? typeEl.value : 'product';
      return `${this.searchBase}?q=${encodeURIComponent(term)}&type=${encodeURIComponent(type)}&options%5Bprefix%5D=last`;
    }

    onSubmit(e) {
      const term = this.input.value.trim();
      // If this exact term was completed during typing (e.g. "rober" -> "robert oatley"),
      // navigate to the completed search so the page matches the instant dropdown.
      if (this._completion && this._completion.from === term && this._completion.to
          && this._completion.to.toLowerCase() !== term.toLowerCase()) {
        e.preventDefault();
        window.location.assign(this.buildPageUrl(this._completion.to));
      }
    }

    /* Discrete action (facet / sort / price / suggestion click): fetch + inject one URL. */
    async fetchResults(url) {
      const reqId = ++this._reqId;
      const r = await this.fetchSectionText(url);
      if (!r || reqId !== this._reqId) return null;
      this.injectText(url, r.sectionHTML);
      return r.count;
    }

    /* Fetch a results URL via the Section Rendering API. Returns { sectionHTML, count }
       (count = search.results_count) or null if the request was aborted/failed. Does NOT
       touch the DOM, so typeSearch can read the count and decide whether to inject. */
    async fetchSectionText(url) {
      this.currentUrl = url;
      this.setLoading(true);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const sep = url.includes('?') ? '&' : '?';
      try {
        const res = await fetch(`${url}${sep}section_id=${this.section}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const tmpl = document.createElement('template');
        tmpl.innerHTML = text;
        const sectionEl = tmpl.content.querySelector(`#shopify-section-${this.section}`) || tmpl.content.querySelector('.iss');
        const issEl = sectionEl && sectionEl.matches('.iss') ? sectionEl : (sectionEl ? sectionEl.querySelector('.iss') : null);
        const count = issEl ? (parseInt(issEl.getAttribute('data-count'), 10) || 0) : 0;
        return { sectionHTML: sectionEl ? sectionEl.innerHTML : text, count };
      } catch (e) {
        if (e.name !== 'AbortError') { this.resultsEl.innerHTML = ''; }
        return null;
      } finally {
        this.setLoading(false);
      }
    }

    /* Inject already-fetched section HTML into the results column and post-process it. */
    injectText(url, sectionHTML) {
      this.currentUrl = url;
      // The section renders filter groups collapsed by default; preserve any the shopper expanded
      // so a facet/sort/price re-fetch doesn't collapse the group they're working in.
      const openKeys = Array.from(this.resultsEl.querySelectorAll('.iss-facet[open]')).map((d) => d.getAttribute('data-facet-key')).filter(Boolean);
      this.resultsEl.innerHTML = sectionHTML;
      // product-card injects a per-card icon-legend modal (.modal/#legendsPopup, position:fixed,
      // full-viewport). In the dropdown context the theme's hide rule doesn't apply, so it can
      // cover the whole screen. We don't want the legend popup in instant search — strip the
      // modals and disarm the icon openers (clicking an icon in the dropdown just does nothing).
      this.resultsEl.querySelectorAll('modal-dialog, .modal, [id="legendsPopup"]').forEach((m) => m.remove());
      this.resultsEl.querySelectorAll('modal-opener').forEach((o) => o.removeAttribute('data-modal'));
      // product-card images are lazy-loaded (data-src/data-srcset); the theme's lazyload
      // observer doesn't watch injected nodes, so they stay blank. Promote them to load now.
      this.resultsEl.querySelectorAll('img[data-src], source[data-srcset], img[data-srcset]').forEach((el) => {
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
        if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
        el.classList.remove('no-js-hidden', 'lazyload');
      });
      openKeys.forEach((k) => {
        const d = this.resultsEl.querySelector(`.iss-facet[data-facet-key="${k.replace(/["\\]/g, '\\$&')}"]`);
        if (d) d.open = true;
      });
      this.syncViewAll();
      this.open();
    }

    /* Query suggestions (left column) via the Predictive Search API.
       Returns the top suggestion text (used by typeSearch to complete a partial word), or null. */
    async fetchSuggestions(term) {
      if (!this.suggestionsEl) return null;
      try {
        if (this.suggestController) this.suggestController.abort();
        this.suggestController = new AbortController();
        const base = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
        const res = await fetch(`${base}.json?q=${encodeURIComponent(term)}&resources%5Btype%5D=query&resources%5Blimit%5D=6`, { signal: this.suggestController.signal });
        if (!res.ok) return null;
        const data = await res.json();
        const queries = (data.resources && data.resources.results && data.resources.results.queries) || [];
        let html = `<h4 class="instant-search__col-title">Suggestions</h4><ul class="instant-search__suggest-list" role="list">`;
        if (!queries.length) {
          html += `<li><button type="button" class="instant-search__suggest" data-term="${this.escape(term)}">${this.escape(term)}</button></li>`;
        } else {
          queries.forEach((q) => {
            const t = q.text || term;
            html += `<li><button type="button" class="instant-search__suggest" data-term="${this.escape(t)}">${q.styled_text || this.escape(t)}</button></li>`;
          });
        }
        html += `</ul>`;
        this.suggestionsEl.innerHTML = html;
        return queries.length ? (queries[0].text || null) : null;
      } catch (e) { return null; }
    }

    onSuggestionClick(e) {
      const btn = e.target.closest('.instant-search__suggest');
      if (!btn) return;
      e.preventDefault();
      const term = btn.getAttribute('data-term');
      this.input.value = term;
      this.fetchResults(this.buildSearchUrl(term));
    }

    onResultsClick(e) {
      // Adding to cart from a result opens the quick-add / cart drawer — close the dropdown so it
      // doesn't overlap. Don't preventDefault: let the theme's quick-add handler run.
      if (e.target.closest('.js-quick-add')) { setTimeout(() => this.close(), 0); return; }

      const facet = e.target.closest('[data-facet-link]');
      if (facet) { e.preventDefault(); this.fetchResults(facet.getAttribute('href')); return; }

      const more = e.target.closest('[data-facet-more]');
      if (more) {
        e.preventDefault();
        const group = more.closest('[data-facet]');
        group.querySelectorAll('.iss-facet__item--extra').forEach((li) => li.classList.remove('is-hidden'));
        more.remove();
        return;
      }

      const priceGo = e.target.closest('[data-price-go]');
      if (priceGo) {
        e.preventDefault();
        this.applyPrice(priceGo.closest('[data-price-facet]'));
        return;
      }

      const sortOpt = e.target.closest('[data-sort-value]');
      if (sortOpt) {
        e.preventDefault();
        this.panel.classList.remove('iss--show-sort');
        const url = this.setParam(this.currentUrl || `${this.searchBase}?q=${encodeURIComponent(this.input.value.trim())}`, 'sort_by', sortOpt.getAttribute('data-sort-value'));
        this.fetchResults(url);
        return;
      }
      // [data-viewall] is a normal link (href kept in sync) — allow default navigation.
    }

    onResultsChange(e) {
      const sortSel = e.target.closest('[data-sort-select]');
      if (!sortSel) return;
      const url = this.setParam(this.currentUrl || `${this.searchBase}?q=${encodeURIComponent(this.input.value.trim())}`, 'sort_by', sortSel.value);
      this.fetchResults(url);
    }

    applyPrice(wrap) {
      if (!wrap) return;
      const tpl = wrap.getAttribute('data-url-template') || this.currentUrl;
      const minEl = wrap.querySelector('[data-price-min]');
      const maxEl = wrap.querySelector('[data-price-max]');
      let url = tpl;
      if (minEl) url = minEl.value ? this.setParam(url, minEl.name, minEl.value) : this.removeParam(url, minEl.name);
      if (maxEl) url = maxEl.value ? this.setParam(url, maxEl.name, maxEl.value) : this.removeParam(url, maxEl.name);
      this.fetchResults(url);
    }

    onPanelChrome(e) {
      const filterBtn = e.target.closest('[data-iss-filter]');
      if (filterBtn) { this.panel.classList.toggle('iss--show-facets'); return; }
      const sortBtn = e.target.closest('[data-iss-sort]');
      if (sortBtn) { this.panel.classList.toggle('iss--show-sort'); return; }
      const sheetClose = e.target.closest('[data-iss-sheet-close]');
      if (sheetClose) { this.panel.classList.remove('iss--show-facets', 'iss--show-sort'); return; }
      const closeBtn = e.target.closest('[data-iss-close]');
      if (closeBtn) { this.close(); return; }
    }

    syncViewAll() {
      const va = this.resultsEl.querySelector('[data-viewall]');
      if (va && this.currentUrl) va.setAttribute('href', this.currentUrl);
    }

    setParam(url, key, val) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set(key, val);
      return u.pathname + u.search;
    }
    removeParam(url, key) {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete(key);
      return u.pathname + u.search;
    }
    escape(s) { return (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    setLoading(on) { this.panel.classList.toggle('is-loading', !!on); }

    open() {
      this.panel.hidden = false;
      this.setAttribute('open', '');
      document.body.classList.add('overlay-instant-search');
      this.input.setAttribute('aria-expanded', 'true');
      // On mobile the panel is full-screen below the search input — anchor its top to the input.
      if (window.matchMedia('(max-width: 767.98px)').matches) {
        const r = this.input.getBoundingClientRect();
        this.panel.style.top = Math.max(0, Math.round(r.bottom)) + 'px';
      } else {
        this.panel.style.top = '';
      }
    }
    close() {
      this._reqId++;                         // invalidate any in-flight request so it can't re-open the panel
      if (this.controller) this.controller.abort();
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.panel.classList.remove('iss--show-facets', 'iss--show-sort');
      document.body.classList.remove('overlay-instant-search');
      this.input.setAttribute('aria-expanded', 'false');
    }
    clear() { this._reqId++; if (this.controller) this.controller.abort(); this.input.value = ''; this.resultsEl.innerHTML = ''; if (this.suggestionsEl) this.suggestionsEl.innerHTML = ''; this.close(); }
  }

  customElements.define('instant-search', InstantSearch);
}
